import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route } from 'react-router-dom';

import { FullScreenLoader } from './components/layout/FullScreenLoader.js';
import { Layout } from './components/layout/Layout.js';
import { NavigationBridge } from './components/layout/NavigationBridge.js';
import { RequirePsicoAccess } from './components/layout/RequirePsicoAccess.js';
import { RequireRol } from './components/layout/RequireRol.js';
import { AuthProvider, useAuth } from './hooks/useAuth.js';
import { AspiranteDetalle } from './pages/aspirantes/AspiranteDetalle.js';
import { AspirantesPage } from './pages/aspirantes/AspirantesPage.js';
import { AniosServicioDetalle } from './pages/anios-servicio/AniosServicioDetalle.js';
import { AniosServicioPage } from './pages/anios-servicio/AniosServicioPage.js';
import { AprobacionAusenciasPage } from './pages/ausencias/AprobacionAusenciasPage.js';
import { AusenciasPage } from './pages/ausencias/AusenciasPage.js';
import { CatalogosPage } from './pages/catalogos/CatalogosPage.js';
import { ClienteDetalle } from './pages/clientes/ClienteDetalle.js';
import { ClientesPage } from './pages/clientes/ClientesPage.js';
import { NuevoCliente } from './pages/clientes/NuevoCliente.js';
import { CxCPage } from './pages/cxc/CxCPage.js';
import { ReportesPage } from './pages/reportes/ReportesPage.js';
import { DiasFestivosDetalle } from './pages/dias-festivos/DiasFestivosDetalle.js';
import { DiasFestivosPage } from './pages/dias-festivos/DiasFestivosPage.js';
import { Login } from './pages/Login.js';
import { NotFound } from './pages/NotFound.js';
import { SeguimientosPage } from './pages/seguimientos/SeguimientosPage.js';
import { SinAccesoPage } from './pages/SinAcceso.js';
import { NuevoTicket } from './pages/tickets/NuevoTicket.js';
import { TicketDetalle } from './pages/tickets/TicketDetalle.js';
import { TicketsDashboardPage } from './pages/tickets/TicketsDashboardPage.js';
import { TicketsPage } from './pages/tickets/TicketsPage.js';
import { TipoAusenciaDetalle } from './pages/tipos-ausencia/TipoAusenciaDetalle.js';
import { TiposAusenciaPage } from './pages/tipos-ausencia/TiposAusenciaPage.js';
import { SecretosPage } from './pages/secretos/SecretosPage.js';
import { UsuarioDetalle } from './pages/usuarios/UsuarioDetalle.js';
import { UsuariosPage } from './pages/usuarios/UsuariosPage.js';

function RootRedirect() {
  const { isAuthenticated, status } = useAuth();
  // 'checking': todavía se está intentando el silent refresh (SRS-01 §4.1) —
  // no decidir la ruta hasta saber si el refreshToken httpOnly sigue vivo.
  if (status === 'checking') return <FullScreenLoader />;
  return <Navigate to={isAuthenticated ? '/clientes' : '/login'} replace />;
}

// AuthProvider necesita estar dentro del árbol del router (usa useNavigate() —
// ver useAuth.tsx), así que ahora vive en el elemento raíz de las rutas en vez
// de envolver <App/> en main.tsx (requisito para migrar a createBrowserRouter:
// useBlocker/useBeforeUnload — guard de salida de Días Festivos, Prompt AC-3 —
// solo funcionan con un "data router", no con el <BrowserRouter/> declarativo
// que se usaba antes).
function RootLayout() {
  return (
    <AuthProvider>
      <NavigationBridge />
      <Outlet />
    </AuthProvider>
  );
}

export const router: ReturnType<typeof createBrowserRouter> = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<Login />} />
      <Route path="/sin-acceso" element={<SinAccesoPage />} />

        <Route element={<Layout />}>
          <Route path="/clientes" element={<ClientesPage />} />
          <Route path="/clientes/nuevo" element={<NuevoCliente />} />
          <Route
            path="/clientes/:id/editar"
            element={
              <RequireRol roles={['root', 'head', 'analista', 'kam', 'ads_manager', 'gerente_comercial']}>
                <NuevoCliente />
              </RequireRol>
            }
          />
          <Route path="/clientes/:id" element={<ClienteDetalle />} />
          <Route path="/cxc" element={<CxCPage />} />
          <Route
            path="/reportes"
            element={
              <RequireRol roles={['root', 'head', 'analista', 'kam', 'ads_manager', 'gerente_comercial', 'comercial', 'CEO']}>
                <ReportesPage />
              </RequireRol>
            }
          />
          <Route path="/ausencias" element={<AusenciasPage />} />
          <Route path="/aprobacion-ausencias" element={<AprobacionAusenciasPage />} />
          <Route path="/anios-servicio" element={<AniosServicioPage />} />
          <Route path="/anios-servicio/:id" element={<AniosServicioDetalle />} />
          <Route
            path="/catalogos"
            element={
              // Prompt AA: kam/comercial entran para gestionar Áreas, aunque
              // vean Giro/Origen/Plataforma/Tiers/Tareas-por-Área en solo
              // lectura (ver CatalogosPage.tsx, puedeGestionar por card).
              <RequireRol roles={['root', 'head', 'analista', 'kam', 'ads_manager', 'gerente_comercial', 'comercial', 'CEO']}>
                <CatalogosPage />
              </RequireRol>
            }
          />
          <Route
            path="/secretos"
            element={
              <RequireRol roles={['root']}>
                <SecretosPage />
              </RequireRol>
            }
          />
          <Route
            path="/usuarios"
            element={
              <RequireRol roles={['root', 'CEO']}>
                <UsuariosPage />
              </RequireRol>
            }
          />
          <Route
            path="/usuarios/nuevo"
            element={
              // Prompt BB-3(RH) — solo root crea (canManageUsers=false para
              // CEO, igual que kam — POST /usuarios sigue siendo root-only).
              <RequireRol roles={['root']}>
                <UsuarioDetalle />
              </RequireRol>
            }
          />
          <Route
            path="/usuarios/:id"
            element={
              <RequireRol roles={['root', 'CEO']}>
                <UsuarioDetalle />
              </RequireRol>
            }
          />
          <Route
            path="/seguimientos"
            element={
              <RequireRol roles={['root', 'head', 'analista', 'kam', 'ads_manager', 'gerente_comercial', 'CEO']}>
                <SeguimientosPage />
              </RequireRol>
            }
          />
          <Route
            path="/dias-festivos"
            element={
              <RequireRol roles={['root', 'head']}>
                <DiasFestivosPage />
              </RequireRol>
            }
          />
          <Route
            path="/dias-festivos/nuevo"
            element={
              <RequireRol roles={['root', 'head']}>
                <DiasFestivosDetalle />
              </RequireRol>
            }
          />
          <Route
            path="/dias-festivos/:id"
            element={
              <RequireRol roles={['root', 'head']}>
                <DiasFestivosDetalle />
              </RequireRol>
            }
          />
          <Route
            path="/tipos-ausencia"
            element={
              <RequireRol roles={['root', 'head']}>
                <TiposAusenciaPage />
              </RequireRol>
            }
          />
          <Route
            path="/tipos-ausencia/nuevo"
            element={
              <RequireRol roles={['root', 'head']}>
                <TipoAusenciaDetalle />
              </RequireRol>
            }
          />
          <Route
            path="/tipos-ausencia/:id"
            element={
              <RequireRol roles={['root', 'head']}>
                <TipoAusenciaDetalle />
              </RequireRol>
            }
          />
          <Route
            path="/aspirantes"
            element={
              <RequirePsicoAccess>
                <AspirantesPage />
              </RequirePsicoAccess>
            }
          />
          <Route
            path="/aspirantes/:id"
            element={
              <RequirePsicoAccess>
                <AspiranteDetalle />
              </RequirePsicoAccess>
            }
          />
          <Route path="/tickets" element={<TicketsPage />} />
          <Route path="/tickets/nuevo" element={<NuevoTicket />} />
          <Route path="/tickets/dashboard" element={<TicketsDashboardPage />} />
          <Route path="/tickets/:id" element={<TicketDetalle />} />
        </Route>

        <Route path="*" element={<NotFound />} />
      </Route>
    ),
  );

